/**
 * Converts byte array to base64url encoding (URL-safe base64)
 */
function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/**
 * Converts base64url encoding back to byte array
 */
function fromBase64Url(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * Compresses text using gzip (browser native CompressionStream)
 */
async function gzip(text: string): Promise<Uint8Array> {
  const cs = new CompressionStream('gzip');
  const stream = new Blob([text]).stream().pipeThrough(cs);
  const arrayBuffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

/**
 * Decompresses gzipped bytes to text (browser native DecompressionStream)
 */
async function gunzip(bytes: Uint8Array): Promise<string> {
  const ds = new DecompressionStream('gzip');
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(ds);
  return new Response(stream).text();
}

/**
 * Compresses and encodes an array of strings for URL usage
 * Uses native browser gzip compression with base64url encoding when available,
 * falls back to plain URL-encoded JSON for older browsers
 * 
 * @param items - Array of strings to compress
 * @returns Promise resolving to encoded string with format prefix (gz. or u.)
 */
export async function compressStringArray(items: string[]): Promise<string> {
  try {
    const json = JSON.stringify(items);
    
    // Use native compression if available (modern browsers)
    if ('CompressionStream' in window && 'DecompressionStream' in window) {
      const compressed = await gzip(json);
      return 'gz.' + toBase64Url(compressed);
    }
    
    // Fallback: plain URL-encoded JSON
    return 'u.' + encodeURIComponent(json);
  } catch (error) {
    console.error('Error compressing string array:', error);
    return '';
  }
}

/**
 * Decompresses and decodes a URL parameter back to an array of strings
 * Handles both gzip-compressed (gz.) and plain URL-encoded (u.) formats
 * Also supports legacy format without scheme prefix for backward compatibility
 * 
 * @param encoded - Encoded string from URL parameter
 * @returns Promise resolving to array of decoded strings
 */
export async function decompressStringArray(encoded: string): Promise<string[]> {
  try {
    if (!encoded) return [];
    
    const [scheme, data] = encoded.split('.', 2);
    
    if (scheme === 'gz') {
      // Gzip-compressed format
      const bytes = fromBase64Url(data);
      const json = await gunzip(bytes);
      return JSON.parse(json);
    }
    
    if (scheme === 'u') {
      // Plain URL-encoded format
      return JSON.parse(decodeURIComponent(data));
    }
    
    // Legacy format without scheme prefix (assume plain JSON)
    return JSON.parse(decodeURIComponent(encoded));
  } catch (error) {
    console.error('Error decompressing string array:', error);
    return [];
  }
}
